import React, {useCallback, useContext, useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import {Button} from '@mui/material';
import BorderColorRoundedIcon from '@mui/icons-material/BorderColorRounded';
import {DispatchContext, StateContext} from '../../../store';
import RejectArtefact from './RejectArtefact';
import ForwardToCurator from './ForwardToCurator';
import AcceptArtefact from './AcceptArtefact';
import ReplyIcon from '@mui/icons-material/Reply';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadDoneRoundedIcon from '@mui/icons-material/DownloadDoneRounded';
import {catalogueTaskPoolService, catalogueTransition} from '../../../services/CatalogueServices';
import {taskPoolServices} from '../../../services/TaskPoolServices';
import {SNACKBAR_OPEN} from '../../../reducers';

export default function ArtefactsDetailActions({item, handleEdit, catalogueId}) {
  const dispatch = useContext(DispatchContext);
  const {role} = useContext(StateContext);
  const {user} = useContext(StateContext);
  const {commonUpdate} = useContext(StateContext);
  const [taskPool, setTaskPool] = useState([]);
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [display, setDisplay] = useState(false);
  const internVoluenteer = role?.id === 5 || role?.id === 6;
  const curatorAssistant = role?.id === 2 || role?.id === 3;
  const roleAssistant = role?.id === 3;
  const roleCurator = role?.id === 2;
  const roleAdmin = role?.id === 1 || role?.id === 10;
  const roleMuseumChief = role?.id === 10;
  const requested = item?.state === 'requested';
  const rejected = item?.state === 'rejected';
  const approved = item?.state === 'approved';
  const resubmitted = item?.state === 'resubmitted';
  const rejectedForChanges = item?.state === 'rejected_for_changes';
  // Set by the 'initiate_edit' transition once a curator/chief has started editing an
  // approved catalogue — stays until the edit is submitted (resubmitted) or abandoned.
  const inProgress = item?.state === 'in_progress';
  const roleId = role?.id;
  const userName = user?.first_name +' '+ user?.last_name;

  let isAssistant = false;

  if (userName === item?.user_name) {
    isAssistant = true
  }

  // Match the task pool row belonging to the current logged-in user, not just their
  // role — a catalogue can have multiple task pool rows for the same role (e.g. after
  // resubmission), so matching by role alone can pick a stale/unrelated row.
  const myTaskPool = taskPool?.find(value => value?.user_id === user?.id);

  const taskPoolId = myTaskPool?.id || null;
  const taskPoolState = myTaskPool?.state || '';

  // Mirrors the backend's "active_task" definition (task_pools.state in [assigned, in_progress])
  // used to populate the NEW TASK tab — an active task cannot be edited, only past tasks can.
  const isActiveTask = taskPoolState === 'assigned' || taskPoolState === 'in_progress';

  // Whether a more specific, item-state-driven badge/action set is already being shown
  // below — if so, the generic Completed/Pending fallback badge must not also render,
  // or two status buttons (e.g. "Completed" and "Approved") end up displayed together.
  const hasSpecificStatusBadge =
    (internVoluenteer && (requested || rejected || approved || resubmitted)) ||
    (roleAssistant && isAssistant && (rejected || resubmitted)) ||
    rejectedForChanges ||
    (approved && (roleCurator || roleMuseumChief) && !isActiveTask) ||
    (inProgress && (roleCurator || roleMuseumChief) && !isActiveTask) ||
    ((roleCurator || roleAdmin) && resubmitted && !isActiveTask);

  const getTaskPool = useCallback(() => {
    catalogueTaskPoolService('get', catalogueId).then(res => {
      setTaskPool(res?.data);
    })
  }, [catalogueId]);

  useEffect(() => {
    getTaskPool();
  }, [commonUpdate, getTaskPool]);

  const handleReject = () => {
    setOpen(true);
  }

  const handleClose = () => {
    setOpen(false);
  }

  const handleOpenAction = () => {
    setShow(true);
  }

  const handleExitAction = () => {
    setShow(false);
  }

  const handleAcceptAction = () => {
    setDisplay(true);
  }

  const handleCloseAction = () => {
    setDisplay(false);
  }

  const handleInitiateEdit = () => {
    catalogueTransition('put', catalogueId, {action: 'initiate_edit'}).then(() => {
      dispatch({type: 'STATE', payload: {change: true}});
      dispatch({
        type: SNACKBAR_OPEN,
        payload: {isNotify: true, severity: 'success', message: 'Catalogue opened for editing'}
      });
      window.location.reload();
    }).catch(() => {
      dispatch({
        type: SNACKBAR_OPEN,
        payload: {
          isNotify: true,
          severity: 'error',
          message: 'Unable to open this catalogue for editing. It may have changed state since you opened this page.'
        }
      });
    });
  }

  const handleStartPool = () => {
    taskPoolServices('put', taskPoolId, {action: 'start'}).then(res => {
      dispatch({type: 'STATE', payload: {change: true}});
      dispatch({
        type: SNACKBAR_OPEN,
        payload: {isNotify: true, severity: 'success', message: 'Started successfully'}
      });
      window.location.reload();
    });
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      {item?.state &&
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        {(internVoluenteer && requested) ? (
          <Box>
            <Button variant={'contained'} className={'shadow-none no-cursor'}
                    sx={{mr: 2, bgcolor: '#FF965B', '&:hover': {bgcolor: '#FF965B'}}}>Pending</Button>
          </Box>) : (internVoluenteer && rejected) ? (
          <Box>
            <Button variant={'contained'} className={'shadow-none no-cursor'}
                    sx={{mr: 2, bgcolor: '#f6363c', '&:hover': {bgcolor: '#F6363C'}}}>Rejected</Button>
            <Button onClick={handleEdit}
                    startIcon={<BorderColorRoundedIcon/>} variant={'outlined'}
                    sx={{mr: 2}}>Resubmit</Button>
          </Box>) : (internVoluenteer && approved) ? (
          <Box>
            <Button variant={'contained'} className={'shadow-none no-cursor'}
                    sx={{mr: 2, bgcolor: '#009344', '&:hover': {bgcolor: '#009344'}}}>Approved</Button>
          </Box>
        ) : <div/>}
        {taskPoolState === 'in_progress' ?
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {(curatorAssistant && rejected) ? (
              <Box>
                <Button variant={'contained'} className={'shadow-none no-cursor'}
                        sx={{mr: 2, bgcolor: '#f6363c', '&:hover': {bgcolor: '#F6363C'}}}>Rejected</Button>
              </Box>
            ) : (curatorAssistant && approved) ? (
              <Box>
                <Button variant={'contained'} className={'shadow-none no-cursor'}
                        sx={{mr: 2, bgcolor: '#009344', '&:hover': {bgcolor: '#009344'}}}>Approved</Button>
              </Box>
            ) : (rejectedForChanges && isAssistant) ? (
              <Box>
                <Button onClick={handleEdit}
                        startIcon={<BorderColorRoundedIcon/>} variant={'outlined'}
                        sx={{mr: 2}}>Edit</Button>
              </Box>
            ) : curatorAssistant ? (
              <Box>
                {/* An assistant only escalates an intern/volunteer's submission onward —
                    the curator is the one with reject/approve authority over it. */}
                {roleCurator && (
                  <Button onClick={handleReject} startIcon={<ClearIcon/>} variant={'contained'} color={'error'}
                          sx={{mr: 2}}>Reject</Button>
                )}
                {roleCurator &&
                <Button onClick={handleAcceptAction} sx={{bgcolor: '#009344'}} startIcon={<DownloadDoneRoundedIcon/>}
                        variant={'contained'}>Approve</Button>}
                {roleAssistant &&
                <Button onClick={handleOpenAction} startIcon={<ReplyIcon/>} variant={'contained'}>
                  Forward to Curator
                </Button>
                }
              </Box>
            ) : <div/>}
          </Box> :
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {curatorAssistant && (taskPoolState === 'in_progress' || taskPoolState === 'assigned') ?
              <Button onClick={handleStartPool} variant={'contained'} sx={{ml: 2}}>
                Start
              </Button> :
              <div>
                {taskPoolState && !hasSpecificStatusBadge &&
                <div>
                  {taskPoolState === 'completed' ?
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button variant={'contained'} className={'shadow-none no-cursor'}
                              sx={{mr: 2, bgcolor: '#009344', '&:hover': {bgcolor: '#009344'}}}>Completed</Button>
                    </Box> :
                    <Button variant={'contained'} className={'shadow-none no-cursor'}
                            sx={{mr: 2, bgcolor: '#FF965B', '&:hover': {bgcolor: '#FF965B'}}}>Pending</Button>
                  }
                </div>}
              </div>
            }
          </Box>
        }
        {(rejected && roleAssistant && isAssistant) ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant={'contained'} className={'shadow-none no-cursor'}
                    sx={{mr: 2, bgcolor: '#f6363c', '&:hover': {bgcolor: '#F6363C'}}}>Rejected</Button>
            {!isActiveTask && (
              <Button onClick={handleEdit}
                      startIcon={<BorderColorRoundedIcon/>} variant={'outlined'}
                      sx={{mr: 2}}>Edit</Button>
            )}
          </Box>) : (resubmitted && roleAssistant && isAssistant) && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant={'contained'} className={'shadow-none no-cursor'}
                    sx={{mr: 2, bgcolor: '#009344', '&:hover': {bgcolor: '#009344'}}}>Resubmitted</Button>
          </Box>)
        }
      </Box>
      }
      {rejectedForChanges && (
        <Box>
          <Button variant={'contained'} className={'shadow-none no-cursor'}
                  sx={{mr: 2, bgcolor: '#FF965B', '&:hover': {bgcolor: '#FF965B'}}}>Rejected for Changes</Button>
          {/* Volunteers/interns have no task pool "Start" step to clear isActiveTask, so
              only gate on it for roles that do (curator/assistant); the item's owner can
              always edit their own rejected-for-changes catalogue. */}
          {isAssistant && (internVoluenteer || !isActiveTask) && (
            <Button onClick={handleEdit}
                    startIcon={<BorderColorRoundedIcon/>} variant={'outlined'}
                    sx={{mr: 2}}>Edit</Button>
          )}
        </Box>
      )}
      {(internVoluenteer && resubmitted) &&
      <Button variant={'contained'} className={'shadow-none no-cursor'}
              sx={{mr: 2, bgcolor: '#009344', '&:hover': {bgcolor: '#009344'}}}>Resubmitted</Button>}
      {(approved && (roleCurator || roleMuseumChief) && !isActiveTask) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button variant={'contained'} className={'shadow-none no-cursor'}
                  sx={{mr: 2, bgcolor: '#009344', '&:hover': {bgcolor: '#009344'}}}>Approved</Button>
          <Button onClick={handleInitiateEdit}
                  startIcon={<BorderColorRoundedIcon/>} variant={'outlined'}
                  sx={{mr: 2}}>Start Edit</Button>
        </Box>
      )}
      {(inProgress && (roleCurator || roleMuseumChief) && !isActiveTask) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button onClick={handleEdit}
                  startIcon={<BorderColorRoundedIcon/>} variant={'outlined'}
                  sx={{mr: 2}}>Edit</Button>
        </Box>
      )}
      {((roleCurator || roleAdmin) && resubmitted && !isActiveTask) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button onClick={handleEdit}
                  startIcon={<BorderColorRoundedIcon/>} variant={'outlined'}
                  sx={{mr: 2}}>Edit</Button>
          {taskPoolState !== 'completed' && (
            <>
              <Button onClick={handleReject} startIcon={<ClearIcon/>} variant={'contained'} color={'error'}
                      sx={{mr: 2}}>Reject</Button>
              <Button onClick={handleAcceptAction} sx={{bgcolor: '#009344'}} startIcon={<DownloadDoneRoundedIcon/>}
                      variant={'contained'}>Approve</Button>
            </>
          )}
        </Box>
      )}
      {open &&
      <RejectArtefact open={open} handleAction={handleClose} roleId={roleId} catalogueId={catalogueId}
                      taskPool={taskPool}/>}
      {show &&
      <ForwardToCurator open={show} handleAction={handleExitAction} catalogueId={catalogueId}/>}
      {display &&
      <AcceptArtefact open={display} handleAction={handleCloseAction} roleId={roleId} taskPool={taskPool}
                      catalogueId={catalogueId}/>}
    </Box>
  );
}